/**
 * Shared TanStack Query client factory with standard defaults.
 *
 * All React SPA dashboards should use createStandardQueryClient()
 * to ensure consistent caching, retry, and refetch behavior.
 */

import { QueryClient, type QueryClientConfig } from "@tanstack/react-query";
import { AuthError } from "./fetcher";

/** Standard query staleTime — 30s avoids redundant refetches between navigations. */
export const DEFAULT_QUERY_STALE_TIME = 30_000;

/** Standard query retry count for *retryable* failures (network / 5xx). */
export const DEFAULT_QUERY_MAX_RETRIES = 1;

/**
 * 4xx statuses that ARE worth retrying despite being client errors:
 * - 408 Request Timeout — the request never reached a deterministic verdict.
 * - 429 Too Many Requests — transient; TanStack's exponential backoff spaces
 *   the retry out so a single retry is safe and often succeeds.
 */
const RETRYABLE_4XX = new Set([408, 429]);

/** Pull an HTTP-ish status off whatever the queryFn threw, if one is present. */
function errorStatus(error: unknown): number | undefined {
  if (error instanceof AuthError) return error.status;
  if (error != null && typeof error === "object" && "status" in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

/**
 * Default retry predicate shared by every dashboard QueryClient.
 *
 * Never retries deterministic failures — auth errors (401/403, or any
 * {@link AuthError}) and other 4xx client errors. Retrying those only delays
 * surfacing the failure to the user (the 2026-05-04 AI-dashboard incident:
 * an expired CF Access JWT was retried before finally rendering an empty
 * state). Network errors, timeouts (408), rate-limits (429), and 5xx server
 * errors are transient, so they retry up to `maxRetries`.
 *
 * Exported so apps that construct their own QueryClient (rather than using
 * {@link createStandardQueryClient}) can adopt the same policy:
 *   `retry: (count, err) => defaultShouldRetry(count, err)`
 */
export function defaultShouldRetry(
  failureCount: number,
  error: unknown,
  maxRetries: number = DEFAULT_QUERY_MAX_RETRIES
): boolean {
  if (error instanceof AuthError) return false;
  const status = errorStatus(error);
  if (status !== undefined && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status)) {
    return false;
  }
  return failureCount < maxRetries;
}

const STANDARD_DEFAULTS: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_QUERY_STALE_TIME,
      retry: (failureCount, error) => defaultShouldRetry(failureCount, error),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
};

/**
 * Create a QueryClient with Plerity standard defaults.
 *
 * Defaults:
 * - staleTime: 30s (avoid unnecessary refetches)
 * - retry: auth-aware — never retries auth/4xx failures, retries
 *   network/timeout/429/5xx once (see {@link defaultShouldRetry})
 * - refetchOnWindowFocus: false (explicit refresh only)
 *
 * @param overrides - Partial config merged over standard defaults
 *
 * @example
 * ```tsx
 * const queryClient = createStandardQueryClient();
 * <QueryClientProvider client={queryClient}>
 *   <App />
 * </QueryClientProvider>
 * ```
 */
export function createStandardQueryClient(overrides?: QueryClientConfig): QueryClient {
  const merged: QueryClientConfig = {
    defaultOptions: {
      queries: {
        ...STANDARD_DEFAULTS.defaultOptions?.queries,
        ...overrides?.defaultOptions?.queries,
      },
      mutations: {
        ...STANDARD_DEFAULTS.defaultOptions?.mutations,
        ...overrides?.defaultOptions?.mutations,
      },
    },
  };

  return new QueryClient(merged);
}
